import React, { useState } from 'react';
import { 
  Table, 
  TableHeader, 
  TableBody, 
  TableRow, 
  TableHead, 
  TableCell 
} from '../ui/Table';
import { 
  TicketStatusBadge, 
  PriorityIndicator, 
  UserAvatar 
} from './ui';
import { 
  Loader2, 
  ChevronUp, 
  ChevronDown,
  ChevronsUpDown,
  Inbox
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

/**
 * TicketsTable Component
 * Displays tickets in a table format with sorting, loading states, and empty state
 * 
 * Props:
 * - tickets: Array of ticket objects
 * - loading: Boolean for loading state
 * - onTicketClick: Function called when row is clicked
 * - onSort: Function called when column header is clicked for sorting
 * - sortBy: Current sort field
 * - sortDirection: 'asc' or 'desc'
 */
const TicketsTable = ({ 
  tickets = [], 
  loading = false,
  onTicketClick,
  onSort,
  sortBy = 'created_at',
  sortDirection = 'desc'
}) => {
  // Loading skeleton
  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow hover={false}>
              <TableHead className="w-32">Ticket ID</TableHead>
              <TableHead className="w-1/3">Subject</TableHead>
              <TableHead>Requester</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Priority</TableHead>
              <TableHead>Assignee</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Updated</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {[...Array(5)].map((_, idx) => (
              <TableRow key={idx} hover={false}>
                <TableCell>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-24"></div>
                </TableCell>
                <TableCell>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-full"></div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="h-6 w-6 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-20"></div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse w-16"></div>
                </TableCell>
                <TableCell>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-16"></div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="h-6 w-6 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-20"></div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-24"></div>
                </TableCell>
                <TableCell>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-24"></div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  // Empty state
  if (tickets.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
        <div className="text-center py-16">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 mb-4">
            <Inbox className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            No tickets found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Try adjusting your filters or search query
          </p>
        </div>
      </div>
    );
  }

  // Helper to render sort icon
  const renderSortIcon = (field) => {
    if (sortBy !== field) {
      return <ChevronsUpDown className="w-4 h-4 ml-1 opacity-50" />;
    }
    return sortDirection === 'asc' 
      ? <ChevronUp className="w-4 h-4 ml-1" />
      : <ChevronDown className="w-4 h-4 ml-1" />;
  };

  // Format date with relative time
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      const relative = formatDistanceToNow(date, { addSuffix: true });
      return relative;
    } catch (error) {
      return dateString;
    }
  };

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow hover={false}>
              {/* Ticket ID */}
              <TableHead 
                className="w-32 cursor-pointer select-none"
                onClick={() => onSort && onSort('ticket_number')}
              >
                <div className="flex items-center">
                  Ticket ID
                  {onSort && renderSortIcon('ticket_number')}
                </div>
              </TableHead>

              {/* Subject */}
              <TableHead 
                className="min-w-[300px] cursor-pointer select-none"
                onClick={() => onSort && onSort('subject')}
              >
                <div className="flex items-center">
                  Subject
                  {onSort && renderSortIcon('subject')}
                </div>
              </TableHead>

              {/* Requester */}
              <TableHead>Requester</TableHead>

              {/* Status */}
              <TableHead 
                className="cursor-pointer select-none"
                onClick={() => onSort && onSort('status')}
              >
                <div className="flex items-center">
                  Status
                  {onSort && renderSortIcon('status')}
                </div>
              </TableHead>

              {/* Priority */}
              <TableHead 
                className="cursor-pointer select-none"
                onClick={() => onSort && onSort('priority')}
              >
                <div className="flex items-center">
                  Priority
                  {onSort && renderSortIcon('priority')}
                </div>
              </TableHead>

              {/* Assignee */}
              <TableHead>Assignee</TableHead>

              {/* Created */}
              <TableHead 
                className="cursor-pointer select-none"
                onClick={() => onSort && onSort('created_at')}
              >
                <div className="flex items-center">
                  Created
                  {onSort && renderSortIcon('created_at')}
                </div>
              </TableHead>

              {/* Updated */}
              <TableHead 
                className="cursor-pointer select-none"
                onClick={() => onSort && onSort('updated_at')}
              >
                <div className="flex items-center">
                  Updated
                  {onSort && renderSortIcon('updated_at')}
                </div>
              </TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {tickets.map((ticket) => (
              <TableRow 
                key={ticket.id} 
                className="cursor-pointer transition-colors"
                onClick={() => onTicketClick && onTicketClick(ticket)}
              >
                {/* Ticket ID */}
                <TableCell>
                  <span className="text-sm font-mono text-indigo-600 dark:text-indigo-400 font-medium">
                    {ticket.ticket_number}
                  </span>
                </TableCell>

                {/* Subject */}
                <TableCell>
                  <div className="flex flex-col gap-1">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-1">
                      {ticket.subject}
                    </span>
                    {ticket.category && (
                      <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                        {ticket.category.replace('_', ' ')}
                      </span>
                    )}
                  </div>
                </TableCell>

                {/* Requester */}
                <TableCell>
                  <div className="flex items-center gap-2">
                    <UserAvatar user={ticket.requester} size="sm" />
                    <div className="flex flex-col min-w-0">
                      <span className="text-sm text-gray-900 dark:text-gray-100 truncate">
                        {ticket.requester?.name || 'Unknown'}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {ticket.requester?.email}
                      </span>
                    </div>
                  </div>
                </TableCell>

                {/* Status */}
                <TableCell>
                  <TicketStatusBadge status={ticket.status} />
                </TableCell>

                {/* Priority */}
                <TableCell>
                  <PriorityIndicator priority={ticket.priority} />
                </TableCell>

                {/* Assignee */}
                <TableCell>
                  {ticket.assignee ? (
                    <div className="flex items-center gap-2">
                      <UserAvatar user={ticket.assignee} size="sm" />
                      <span className="text-sm text-gray-900 dark:text-gray-100 truncate">
                        {ticket.assignee.name}
                      </span>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400 dark:text-gray-500 italic">
                      Unassigned
                    </span>
                  )}
                </TableCell>

                {/* Created */}
                <TableCell>
                  <div className="flex flex-col gap-1">
                    <span className="text-sm text-gray-900 dark:text-gray-100">
                      {formatDate(ticket.created_at)}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(ticket.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </TableCell>

                {/* Updated */}
                <TableCell>
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    {formatDate(ticket.updated_at)}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default TicketsTable;
